import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { EmptyState } from '../../../shared/EmptyState';
import { RetryableError } from '../../../shared/RetryableError';
import { MappingDialog, type MappingPrefill } from '../components/MappingDialog';
import { exportErrorMessage } from '../exportErrors';
import { formatDate } from '../exportFormat';
import { companyDimension, companyWageMode, useExportSettings } from '../exportOptionState';
import { DIMENSION_HELP, DIMENSION_LABEL } from '../exportOptions';
import { WAGE_GROUP_DIMENSIONS, type AccountingMapping, type ChartAccount, type EligiblePayroll, type WageGroupDimension, type WageGroupGrid as Grid, type WageGroupRow } from '../exportTypes';
import { accountingGateway } from '../index';
import { useExportCapabilities } from '../useExportCapabilities';
import '../../job-roles/job-roles.css';
import '../accounting-export.css';

type GridState = { kind: 'idle' } | { kind: 'loading' } | { kind: 'ready'; grid: Grid } | { kind: 'error'; message: string };
/** A result belongs to the request that asked for it: a newer request (another payroll or dimension) shows loading until its own answer arrives. */
type Keyed = { key: string; state: GridState };

const STATUS: Record<WageGroupRow['status'], { glyph: string; word: string; className: string }> = {
  Mapped: { glyph: '✔', word: 'Mapped', className: 'ax-badge--ok' },
  Missing: { glyph: '✖', word: 'Missing', className: 'ax-badge--danger' },
  Ambiguous: { glyph: '⚠', word: 'Ambiguous', className: 'ax-badge--warn' },
};

const isDimension = (v: string | null): v is WageGroupDimension => WAGE_GROUP_DIMENSIONS.includes(v as WageGroupDimension);

/**
 * The "By group" wage-mapping grid: one row for every group that is present in the chosen payroll, with how many employees are in it and which account its gross wages
 * post to. A group with no account blocks the export (nothing is posted to a guessed account), so this is where the gaps are closed. Assigning an account adds a new
 * mapping version carrying the group; changing one ends the old version first and keeps it as history.
 */
export function WageGroupGrid() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { caps } = useExportCapabilities();
  const canManage = caps?.canManageMappings !== false;
  const settings = useExportSettings();
  const urlRun = searchParams.get('runId') ?? '';
  const urlDimension = searchParams.get('dimension');
  const dimension: WageGroupDimension = isDimension(urlDimension) ? urlDimension : (companyWageMode(settings) === 'GroupBased' ? companyDimension(settings) : null) ?? 'Department';

  const [payrolls, setPayrolls] = useState<EligiblePayroll[] | 'loading' | 'error'>('loading');
  const [keyed, setKeyed] = useState<Keyed>({ key: '', state: { kind: 'idle' } });
  const [reloadNonce, setReloadNonce] = useState(0);
  const [payrollNonce, setPayrollNonce] = useState(0);
  const [accounts, setAccounts] = useState<ChartAccount[]>([]);
  const [dialog, setDialog] = useState<{ row: WageGroupRow; replaces: AccountingMapping | null } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    accountingGateway.listEligiblePayrolls(undefined, controller.signal)
      .then((l) => { if (!controller.signal.aborted) setPayrolls(l.items); })
      .catch(() => { if (!controller.signal.aborted) setPayrolls('error'); });
    accountingGateway.listChartAccounts({ active: true, pageSize: 500 }, controller.signal)
      .then((page) => { if (!controller.signal.aborted) setAccounts(page.items); })
      .catch(() => { if (!controller.signal.aborted) setAccounts([]); });
    return () => controller.abort();
  }, [payrollNonce]);

  const list = useMemo(() => (Array.isArray(payrolls) ? payrolls : []), [payrolls]);
  const run = list.find((r) => r.payrollRunId === urlRun) ?? list[0] ?? null;
  const runId = run?.payrollRunId ?? '';

  useEffect(() => {
    if (!runId) return;
    const controller = new AbortController();
    const key = `${runId}|${dimension}|${reloadNonce}`;
    accountingGateway.getWageGroups({ payrollRunId: runId, dimension }, controller.signal)
      .then((grid) => { if (!controller.signal.aborted) setKeyed({ key, state: { kind: 'ready', grid } }); })
      .catch((err) => { if (!controller.signal.aborted) setKeyed({ key, state: { kind: 'error', message: exportErrorMessage(err, 'Could not load the groups in this payroll.') } }); });
    return () => controller.abort();
  }, [runId, dimension, reloadNonce]);
  const state: GridState = keyed.key === `${runId}|${dimension}|${reloadNonce}` ? keyed.state : { kind: 'loading' };

  const setParam = useCallback((key: string, value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set(key, value);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  async function openDialog(row: WageGroupRow) {
    setRowError(null);
    let replaces: AccountingMapping | null = null;
    if (row.mappingId) {
      try {
        replaces = (await accountingGateway.listMappings({ category: 'EarningsGross', activeOnly: true, pageSize: 200 })).items.find((m) => m.id === row.mappingId) ?? null;
      } catch (err) {
        setRowError(exportErrorMessage(err, 'The current mapping could not be read, so it cannot be replaced right now.'));
        return;
      }
    }
    setDialog({ row, replaces });
  }

  if (payrolls === 'loading') return <p role="status" className="muted">Loading finalized payrolls…</p>;
  if (payrolls === 'error') return <RetryableError message="Could not load the finalized payrolls." onRetry={() => { setPayrolls('loading'); setPayrollNonce((n) => n + 1); }} />;
  if (list.length === 0) return <EmptyState message="There are no finalized payrolls yet. The groups shown here are the groups present in a finalized payroll." action={<Link to="/payroll-review">Open Payroll Review</Link>} />;

  const prefill = (row: WageGroupRow): MappingPrefill => ({
    category: 'EarningsGross', groupDimension: dimension, groupKey: row.groupKey, groupLabel: row.label, effectiveFrom: run?.periodStart ?? '',
    subject: `Wage account for ${DIMENSION_LABEL[dimension]} group ${row.label}`,
  });

  const grid = state.kind === 'ready' ? state.grid : null;

  return (
    <div data-testid="wage-group-grid">
      <h4 id="ax-wg-title">Wages by group</h4>
      <p className="muted ax-lead">
        Used when the export option is <strong>By group</strong>. Each row is a group of employees in the payroll you choose; its gross wages post to the account you assign. A group with no
        account blocks the export, so nothing is posted to a guessed account.
      </p>
      {notice && <p className="jr-notice jr-notice--ok" role="status" data-testid="wage-group-notice">{notice}</p>}
      {rowError && <p className="jr-error jr-error--block" role="alert">{rowError}</p>}

      <div className="ax-toolbar">
        <div className="jr-field">
          <label htmlFor="ax-wg-run">Payroll (check date)</label>
          <select id="ax-wg-run" value={runId} onChange={(e) => setParam('runId', e.target.value)}>
            {list.map((r) => <option key={r.payrollRunId} value={r.payrollRunId}>{formatDate(r.checkDate)}, {r.payScheduleLabel}, {r.employeeCount} employees</option>)}
          </select>
        </div>
        <div className="jr-field">
          <label htmlFor="ax-wg-dim">Group by</label>
          <select id="ax-wg-dim" value={dimension} onChange={(e) => setParam('dimension', e.target.value)} aria-describedby="ax-wg-dim-help" data-testid="wage-group-dimension">
            {WAGE_GROUP_DIMENSIONS.map((d) => <option key={d} value={d}>{DIMENSION_LABEL[d]}</option>)}
          </select>
        </div>
      </div>
      <p id="ax-wg-dim-help" className="jr-hint">{DIMENSION_HELP[dimension]} Attributes are the employee&apos;s current values, not the values on the check date.</p>
      {caps && !caps.canManageMappings && <p className="jr-readonly" role="note">You can view the groups, but assigning accounts needs permission to manage GL mappings.</p>}

      {state.kind === 'loading' && <p role="status" className="muted">Loading the groups in this payroll…</p>}
      {state.kind === 'error' && <RetryableError message={state.message} onRetry={() => setReloadNonce((n) => n + 1)} />}

      {grid && grid.groups.length === 0 && <p className="muted" data-testid="no-groups">No employee in this payroll has a value for {DIMENSION_LABEL[dimension].toLowerCase()}.</p>}
      {grid && grid.groups.length > 0 && (
        <div className="jr-table-scroll" role="region" aria-label="Wage groups table" tabIndex={0}>
          <table className="jr-table jr-table--actions" data-testid="wage-groups-table">
            <caption className="ax-caption">Groups present in this payroll, by {DIMENSION_LABEL[dimension].toLowerCase()} ({grid.groups.length})</caption>
            <thead>
              <tr>
                <th scope="col">Group</th>
                <th scope="col" className="num">Employees</th>
                <th scope="col">Wage expense account</th>
                <th scope="col">Status</th>
                {canManage && <th scope="col">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {grid.groups.map((g) => {
                const s = STATUS[g.status];
                return (
                  <tr key={g.groupKey} data-testid={`wage-group-row-${g.groupKey}`}>
                    <th scope="row">{g.label}</th>
                    <td className="num">{g.employeeCount}</td>
                    <td>{g.account ? <><strong>{g.account.accountNumber}</strong> {g.account.accountName}{g.mappingVersion !== null && <span className="ax-date jr-hint">Version {g.mappingVersion}</span>}</> : <span className="ax-status--missing">No account</span>}</td>
                    <td><span className={`ax-badge ${s.className}`} data-status={g.status}><span aria-hidden="true">{s.glyph}</span> {s.word}</span></td>
                    {canManage && (
                      <td>
                        {g.status === 'Ambiguous'
                          ? <Link to="/accounting?tab=gl-mapping" aria-label={`Resolve the ambiguous mapping for ${g.label} in the Mappings view`}>Resolve in Mappings</Link>
                          : (
                            <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" onClick={() => void openDialog(g)} data-testid={`assign-${g.groupKey}`}
                              aria-label={`${g.status === 'Mapped' ? 'Change the account for' : 'Assign an account to'} ${g.label}`}>
                              {g.status === 'Mapped' ? 'Change account' : 'Assign account'}
                            </button>
                          )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {grid && grid.employeesWithoutGroup > 0 && (
        <p className="jr-warning" role="note" data-testid="without-group">
          <span aria-hidden="true">⚠ </span>{grid.employeesWithoutGroup} employee{grid.employeesWithoutGroup === 1 ? ' has' : 's have'} no {DIMENSION_LABEL[dimension].toLowerCase()}. When wages are mapped by group, a payroll with such an employee is blocked until
          the employee has one or has an employee-specific override.{dimension === 'EmployeeGroup' && <> <button type="button" className="ax-link-btn" onClick={() => setParam('view', 'employee-groups')}>Manage employee groups</button></>}
        </p>
      )}
      <p className="jr-hint">An existing department mapping keeps working as the group mapping for that department. Mappings are versioned: changing one keeps the old version as history.</p>

      {dialog && (
        <MappingDialog
          title="Assign wage account to group" prefill={prefill(dialog.row)} accounts={accounts} employees={null} departments={null} replaces={dialog.replaces}
          onClose={() => setDialog(null)}
          onSaved={(saved) => {
            setDialog(null);
            setNotice(`Saved a new wage mapping for ${DIMENSION_LABEL[dimension]} group ${dialog.row.label} (version ${saved.mappingVersion}).`);
            setReloadNonce((n) => n + 1);
          }}
        />
      )}
    </div>
  );
}
